import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader, KpiCard, Card } from "@/components/Primitives";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { TrendingUp, AlertTriangle, CheckCircle2, Sparkles } from "lucide-react";

const COLORS = ["#C9A24B", "#059669", "#64748B", "#DC2626", "#3b82f6"];

const tooltipStyle = {
  backgroundColor: "#1E293B",
  border: "1px solid #334155",
  borderRadius: 8,
  color: "#F8FAFC",
  fontSize: 12,
};

export default function Dashboard() {
  const { selectedDept, dateFrom, dateTo } = useApp();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard", {
      params: {
        department_id: selectedDept !== "all" ? selectedDept : undefined,
        date_from: dateFrom, date_to: dateTo,
      },
    }).then((r) => setData(r.data)).catch(() => {});
  }, [selectedDept, dateFrom, dateTo]);

  if (!data) return <div className="text-slate-500">Loading dashboard…</div>;
  const k = data.kpis;

  const insightIcon = { positive: CheckCircle2, warning: AlertTriangle, negative: AlertTriangle };
  const insightColor = { positive: "text-[#059669]", warning: "text-[#C9A24B]", negative: "text-[#DC2626]" };

  return (
    <div>
      <PageHeader title="Training Health" subtitle="Cross-department readiness, at a glance." />

      <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-5 gap-4 mb-6">
        <KpiCard index={0} label="Sessions Completed" value={k.sessions_completed} />
        <KpiCard index={1} label="SOP-Compliant Depts" value={k.fully_compliant_departments} tone="green" />
        <KpiCard index={2} label="Below Standard" value={k.below_standard} tone={k.below_standard ? "red" : "green"} />
        <KpiCard index={3} label="Assessments Pending" value={k.assessments_pending} tone={k.assessments_pending ? "gold" : "default"} />
        <KpiCard index={4} label="Avg Readiness" value={k.avg_readiness} suffix="%" tone="gold" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
        <Card className="lg:col-span-2 p-6">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">Department Readiness Comparison</p>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.charts.department_compare}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
              <XAxis dataKey="department" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} domain={[0, 100]} />
              <Tooltip contentStyle={tooltipStyle} cursor={{ fill: "#1e293b55" }} />
              <Bar dataKey="readiness" radius={[4, 4, 0, 0]}>
                {data.charts.department_compare.map((e, i) => (
                  <Cell key={i} fill={e.readiness >= 90 ? "#059669" : e.readiness < 60 ? "#DC2626" : "#C9A24B"} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">Session Type Breakdown</p>
          {data.charts.session_types.length === 0 ? (
            <p className="text-slate-500 text-sm py-16 text-center">No sessions in range.</p>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie data={data.charts.session_types} dataKey="count" nameKey="type" cx="50%" cy="50%" outerRadius={90} innerRadius={50}>
                  {data.charts.session_types.map((e, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={tooltipStyle} />
              </PieChart>
            </ResponsiveContainer>
          )}
          <div className="flex flex-wrap gap-3 mt-2">
            {data.charts.session_types.map((e, i) => (
              <span key={i} className="text-xs text-slate-400 flex items-center gap-1.5">
                <span className="h-2 w-2 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                {e.type}
              </span>
            ))}
          </div>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4">Training Completion Trend</p>
          <ResponsiveContainer width="100%" height={240}>
            <LineChart data={data.charts.completion_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
              <XAxis dataKey="month" stroke="#64748b" fontSize={11} />
              <YAxis stroke="#64748b" fontSize={11} />
              <Tooltip contentStyle={tooltipStyle} />
              <Line type="monotone" dataKey="sessions" stroke="#C9A24B" strokeWidth={2} dot={{ fill: "#C9A24B", r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6" data-testid="insights-feed">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-4 flex items-center gap-2">
            <Sparkles className="h-3.5 w-3.5 text-[#C9A24B]" /> Auto Insights
          </p>
          <div className="space-y-3">
            {data.insights.length === 0 && <p className="text-slate-500 text-sm">No insights yet.</p>}
            {data.insights.map((ins, i) => {
              const Icon = insightIcon[ins.type] || TrendingUp;
              return (
                <div key={i} className="flex gap-3 items-start">
                  <Icon className={`h-4 w-4 mt-0.5 shrink-0 ${insightColor[ins.type]}`} />
                  <p className="text-sm text-slate-300 leading-snug">{ins.text}</p>
                </div>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
